(() => {
  if (customElements.get('rnf-review-browser')) return;

  class RnfReviewBrowser extends HTMLElement {
    static get observedAttributes() { return ['aria-hidden']; }

    connectedCallback() {
      if (this.connection) return;
      this.connection = new AbortController();
      this.breakpoint = window.matchMedia('(min-width: 750px)');
      this.breakpoint.addEventListener('change', () => this.syncMasonry(), { signal: this.connection.signal });
      window.addEventListener('resize', () => this.syncMasonry(), { signal: this.connection.signal });
      this.syncState();
    }

    attributeChangedCallback(name, oldValue, value) {
      if (oldValue !== value && this.isConnected && this.connection) this.syncState();
    }

    syncState() {
      const open = this.getAttribute('aria-hidden') === 'false';
      if (open === Boolean(this.opened)) return;
      if (open) this.activate();
      else this.deactivate(true);
    }

    activate() {
      this.opened = true;
      this.focusRevision = (this.focusRevision || 0) + 1;
      this.sheet = this.querySelector('.rnf-rv__allSheet');
      this.grid = this.querySelector('.rnf-rv__grid');
      const active = document.activeElement;
      if (active instanceof HTMLElement && active !== document.body && active !== document.documentElement && !this.contains(active) && !active.closest('.rnf-rv__modal')) {
        this.opener = active;
      }
      if (!this.opener?.isConnected) {
        this.opener = document.getElementById(this.id.replace('rnfRvAllModal-', 'rnfRvTop-'));
      }
      this.activeEvents = new AbortController();
      document.addEventListener('keydown', event => this.onKeydown(event), { signal: this.activeEvents.signal });
      this.syncMasonry();
      this.focusFrame = requestAnimationFrame(() => {
        this.focusFrame = 0;
        if (!this.opened || !this.isConnected) return;
        const close = this.querySelector('.rnf-rv__allClose');
        if (close) close.focus({ preventScroll: true });
      });
    }

    deactivate(restoreFocus) {
      this.opened = false;
      this.activeEvents?.abort();
      this.activeEvents = null;
      this.stopMasonry();
      if (this.focusFrame) cancelAnimationFrame(this.focusFrame);
      this.focusFrame = 0;
      const revision = this.focusRevision = (this.focusRevision || 0) + 1;
      if (!restoreFocus) return;
      queueMicrotask(() => {
        if (revision !== this.focusRevision || this.opened || !this.isConnected) return;
        if (document.querySelector('.rnf-rv__modal.is-open, rnf-review-browser[aria-hidden="false"]')) return;
        if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      });
    }

    syncMasonry() {
      if (!this.opened || !this.isConnected) return;
      if (!this.breakpoint.matches) {
        this.stopMasonry();
        return;
      }
      if (!this.grid) return;
      if (!this.resizeObserver) {
        this.cardStyles = new Map();
        this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
        this.resizeObserver.observe(this.grid);
        this.childObserver = new MutationObserver(() => {
          this.observeCards();
          this.scheduleLayout();
        });
        this.childObserver.observe(this.grid, { childList: true });
        this.observeCards();
      }
      this.scheduleLayout();
    }

    observeCards() {
      if (!this.resizeObserver || !this.grid) return;
      const cards = new Set(Array.from(this.grid.children).filter(card => card.matches('.rnf-rv__card2')));
      for (const [card, original] of this.cardStyles) {
        if (cards.has(card)) continue;
        this.resizeObserver.unobserve(card);
        card.style.gridRowEnd = original;
        this.cardStyles.delete(card);
      }
      for (const card of cards) {
        if (this.cardStyles.has(card)) continue;
        this.cardStyles.set(card, card.style.gridRowEnd);
        this.resizeObserver.observe(card);
      }
    }

    scheduleLayout() {
      if (this.layoutFrame || !this.opened || !this.isConnected || !this.breakpoint.matches) return;
      this.layoutFrame = requestAnimationFrame(() => {
        this.layoutFrame = 0;
        if (!this.opened || !this.isConnected || !this.breakpoint.matches || !this.grid?.getBoundingClientRect().width) return;
        const sizes = Array.from(this.cardStyles.keys()).map(card => ({ card, height: card.getBoundingClientRect().height }));
        this.grid.classList.add('is-masonry');
        for (const { card, height } of sizes) {
          if (!height) continue;
          const span = `span ${Math.ceil(height + 12)}`;
          if (card.style.gridRowEnd !== span) card.style.gridRowEnd = span;
        }
      });
    }

    stopMasonry() {
      this.resizeObserver?.disconnect();
      this.childObserver?.disconnect();
      this.resizeObserver = null;
      this.childObserver = null;
      if (this.layoutFrame) cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = 0;
      for (const [card, original] of this.cardStyles || []) card.style.gridRowEnd = original;
      this.cardStyles?.clear();
      this.grid?.classList.remove('is-masonry');
    }

    focusableElements() {
      return Array.from(this.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'))
        .filter(element => !element.closest('[hidden], [inert], [aria-hidden="true"]') && element.getClientRects().length);
    }

    onKeydown(event) {
      if (!this.opened || event.defaultPrevented) return;
      if (event.key === 'Tab') {
        const focusable = this.focusableElements();
        if (!focusable.length) {
          event.preventDefault();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (!this.contains(active) || (event.shiftKey && active === first) || (!event.shiftKey && active === last)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus({ preventScroll: true });
        }
        return;
      }
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const card = event.target instanceof Element ? event.target.closest('.rnf-rv__card2') : null;
      if (!card || event.target !== card || !this.contains(card)) return;
      event.preventDefault();
      event.stopPropagation();
      if (!event.repeat) card.click();
    }

    disconnectedCallback() {
      if (this.isConnected) return;
      this.deactivate(false);
      this.connection?.abort();
      this.connection = null;
      this.breakpoint = null;
    }
  }

  customElements.define('rnf-review-browser', RnfReviewBrowser);
})();
